import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import CollectionCard from "@/components/feature/CollectionCard";
import usePageMeta from "@/hooks/usePageMeta";
import { hotEvents } from "@/mocks/guagua";
import { weeklyArchives } from "@/mocks/weekly";

export default function Weekly() {
  const [activeId, setActiveId] = useState(weeklyArchives[0].id);
  const activeWeek = weeklyArchives.find((w) => w.id === activeId) ?? weeklyArchives[0];

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "网爆吃瓜一周盘点",
        url: "https://yoovx.cn/weekly",
        description: "网爆吃瓜一周盘点汇总帖，按周整理娱乐话题与热门事件，快速补齐一周进度。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://yoovx.cn/" },
          { "@type": "ListItem", position: 2, name: "一周盘点", item: "https://yoovx.cn/weekly" },
        ],
      },
    ],
    []
  );

  usePageMeta({
    title: "网爆吃瓜一周盘点 - 每周热门事件汇总帖",
    description:
      "网爆吃瓜一周盘点汇总帖，按周整理娱乐话题与热门事件，从综艺、剧组到音乐现场与品牌动向，十分钟补齐这一周的吃瓜进度。",
    keywords: "网爆吃瓜一周盘点,网爆吃瓜汇总帖,一周热门事件,吃瓜盘点,娱乐话题汇总",
    path: "/weekly",
    jsonLd,
  });

  const totalHeat = activeWeek.entries.reduce((sum, e) => sum + e.heat, 0);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100/60">
          <div className="w-full px-4 py-10 md:px-8 md:py-14">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-700">
                首页
              </Link>
              <i className="ri-arrow-right-s-line leading-none" aria-hidden="true"></i>
              <span className="text-foreground-800">一周盘点</span>
            </nav>

            <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-5xl">
              网爆吃瓜一周盘点
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              按周把娱乐话题串成一条时间线。每周固定更新，热门事件出现后会第一时间补进对应日期，周末花十分钟就能补齐整周进度。
            </p>

            <div className="mt-7 flex flex-wrap gap-2">
              {weeklyArchives.map((week) => (
                <button
                  key={week.id}
                  type="button"
                  onClick={() => setActiveId(week.id)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeId === week.id
                      ? "bg-foreground-950 text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  {week.label}
                  <span className="ml-2 text-xs opacity-70">{week.range}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 py-10 md:px-8 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-4">
              <div className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6">
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
                  <i className="ri-fire-line leading-none" aria-hidden="true"></i>
                  {activeWeek.label}
                </span>
                <p className="mt-4 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                  {activeWeek.range}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{activeWeek.intro}</p>
                <dl className="mt-5 grid grid-cols-2 gap-4 border-t border-background-200 pt-5">
                  <div>
                    <dt className="text-xs text-foreground-500">收录话题</dt>
                    <dd className="mt-1 font-heading text-2xl font-black text-foreground-950">
                      {activeWeek.entries.length}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-500">累计热度</dt>
                    <dd className="mt-1 font-heading text-2xl font-black text-primary-600">{totalHeat}</dd>
                  </div>
                </dl>
              </div>

              <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">往期盘点</h2>
                <ul className="mt-3 divide-y divide-accent-200/70">
                  {weeklyArchives.map((week) => (
                    <li key={`archive-${week.id}`}>
                      <button
                        type="button"
                        onClick={() => setActiveId(week.id)}
                        className={`flex w-full items-center justify-between py-3 text-left text-sm transition-colors ${
                          activeId === week.id ? "text-primary-700" : "text-foreground-600 hover:text-primary-700"
                        }`}
                      >
                        <span className="font-medium">{week.label}</span>
                        <span className="text-xs text-foreground-500">{week.range}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="lg:col-span-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-3xl">
                {activeWeek.label} 盘点清单
              </h2>
              <p className="mt-2 text-sm text-foreground-600">{activeWeek.intro}</p>

              <ol className="mt-8">
                {activeWeek.entries.map((entry, index) => (
                  <li key={entry.id} className="flex gap-4 md:gap-6">
                    <div className="flex w-16 shrink-0 flex-col items-center md:w-20">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-background-50 bg-primary-500 font-heading text-base font-bold text-background-50 md:h-14 md:w-14 md:text-lg">
                        {entry.day.replace("周", "")}
                      </span>
                      {index < activeWeek.entries.length - 1 && (
                        <span className="my-1 w-px flex-1 bg-background-300" aria-hidden="true"></span>
                      )}
                    </div>
                    <div className="flex-1 pb-8">
                      <div className="rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:p-5">
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 font-medium text-secondary-900">
                            {entry.tag}
                          </span>
                          <span className="text-foreground-500">{entry.date}</span>
                          <span className="inline-flex items-center gap-1 text-primary-600">
                            <i className="ri-fire-line leading-none" aria-hidden="true"></i>
                            {entry.heat}
                          </span>
                        </div>
                        <h3 className="mt-2 font-heading text-base font-bold text-foreground-950 md:text-lg">
                          {entry.title}
                        </h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{entry.summary}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="w-full border-t border-background-200 bg-background-100/60 px-4 py-12 md:px-8 md:py-16">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-3xl">盘点里的热门合集</h2>
            <Link to="/collections" className="text-sm font-medium text-primary-700 transition-colors hover:text-primary-800">
              去合集导航 →
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {hotEvents.slice(0, 3).map((item) => (
              <CollectionCard key={`weekly-${item.id}`} item={item} />
            ))}
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}